<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import moment from "moment-timezone";

import { ROUTES } from '@/constants/routes';
import { formattedAddress } from "@/helpers/address";
import { agencyName } from "@/helpers/agency";
import { formatFullName } from "@/helpers/name";
import { formatPhone } from "@/helpers/phone";
import { useWorkerAddIdentificationStore } from "@/modules/worker/store/workerAddIdentificationStore";
import { useConfigurationStore } from "@/store/configurationStore";

import type { CommonWorker } from "@/types/worker";

import Avatar from "@/dsComponents/Avatar/Avatar.vue";
import { AvatarSize } from "@/dsComponents/Avatar/types";
import Badge from "@/dsComponents/Badge/Badge.vue";
import { BadgeColor } from "@/dsComponents/Badge/types";
import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Link from "@/dsComponents/buttons/Link.vue";
import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";

interface IdentificationWorkerCardProps {
    worker: CommonWorker;
    displayActionButton?: boolean;
}

const props = withDefaults(defineProps<IdentificationWorkerCardProps>(), {
    displayActionButton: false,
});

const { t } = useI18n();
const router = useRouter();
const configurationStore = useConfigurationStore();
const workerAddIdentificationStore = useWorkerAddIdentificationStore();

const showMoreDetails = ref(false);

const inactivityReasons = computed(() => configurationStore.configuration.inactivatedReasons);

const findInactivityReason = (slug: string) => {
    return inactivityReasons.value.find((reason) => reason.slug === slug);
};

const hasDetails = computed(() => {
    return props.worker.nir || props.worker.evoliaId || props.worker.birthDate || props.worker.birthPlace;
});

const isSameFirstnameAndLastname = computed(() => {
    return (
        props.worker.firstname === workerAddIdentificationStore.findWorkerForm.firstname.value &&
        props.worker.lastname === workerAddIdentificationStore.findWorkerForm.lastname.value
    );
});

const isPhoneMatch = computed(() => {
    return formatPhone(props.worker.phone) === formatPhone(workerAddIdentificationStore.findWorkerForm.phone.value);
});

const isEmailMatch = computed(() => {
    return props.worker.email === workerAddIdentificationStore.findWorkerForm.email.value;
});

const isBirthDateMatch = computed(() => {
    return moment(props.worker.birthDate).format("YYYY-MM-DD") === workerAddIdentificationStore.findWorkerForm.birthDate.value;
});

const formattedBirthDate = computed(() => {
    return moment(props.worker.birthDate).format("DD MMM YYYY");
});

const matchIcon = (isMatch: boolean) => {
    return isMatch ? "ti ti-circle-check-filled" : "ti ti-circle-x-filled";
};

const matchClass = (isMatch: boolean, prefix: string) => {
    return isMatch ? `${prefix}--success` : `${prefix}--error`;
};

const handleViewWorkerProfile = () => {
    router.push({ name: ROUTES.APP.WORKERS.PROFIL._ROOT, params: { workerId: props.worker.id } });
};
</script>

<template>
    <div class="identification-worker-card">
        <div class="identification-worker-card__section">
            <div class="identification-worker-card__section__title">
                <Avatar :firstname="worker.firstname" :lastname="worker.lastname" :size="AvatarSize.size40" />
                <div class="identification-worker-card__section__title-infos">
                    <span class="identification-worker-card__section__title-infos-name typo-xm-medium">
                        {{ formatFullName(worker) }}
                        <span
                            :class="[
                                'identification-worker-card__section__title-infos-name-status',
                                matchClass(isSameFirstnameAndLastname, 'identification-worker-card__section__title-infos-name-status'),
                            ]"
                        >
                            <i :class="matchIcon(isSameFirstnameAndLastname)" />
                        </span>
                    </span>
                    <span class="identification-worker-card__section__title-infos-description typo-xxs-regular">
                        {{ t(`page.worker.modal.create.databaseOrigin.${worker.databaseOrigin}`) }}
                    </span>
                </div>
            </div>

            <div v-if="worker.agencies.length > 0" class="identification-worker-card__section__content-item">
                <div class="identification-worker-card__section__content-item-label">
                    <span class="typo-xxs-medium">
                        {{ t("page.worker.modal.create.identification.labels.agency") }}
                    </span>
                </div>

                <div class="identification-worker-card__section__content-item-values">
                    <div
                        v-for="agency of worker.agencies"
                        :key="`identification-worker-card__${worker.id}__agency-${agency.id}`"
                        class="identification-worker-card__section__content-item-value"
                    >
                        <TextInputAlt
                            :model-value="agencyName(agency.name, agency.address)"
                            icon-left="building-estate"
                            variant="grey"
                            state="filled"
                            readonly
                        />
                        <Dropdown v-if="!agency.candidateStatus" :disabled="!agency.inactive" mode="hover" width="auto">
                            <template #trigger-zone>
                                <Badge
                                    :label="agency.inactive ? t('page.worker.status.inactive') : t('page.worker.status.active')"
                                    :color="agency.inactive ? BadgeColor.red : BadgeColor.green"
                                    stroke
                                />
                            </template>

                            <template #dropdown-content>
                                <span class="typo-xm-medium">
                                    {{
                                        t(
                                            "global.inactiveSince",
                                            { date: moment(agency.inactiveReason?.timestamp).format("DD/MM/YYYY") },
                                            { escapeParameter: false }
                                        )
                                    }}
                                </span>
                                <span
                                    v-if="agency.inactiveReason?.reasonSlug && findInactivityReason(agency.inactiveReason.reasonSlug)"
                                    class="typo-xs-regular"
                                >
                                    {{ `${t("global.motif")} : ${findInactivityReason(agency.inactiveReason.reasonSlug)?.reason ?? ""}` }}
                                </span>
                                <span v-if="agency.inactiveReason?.complement" class="typo-xs-regular">
                                    {{ agency.inactiveReason?.complement }}
                                </span>
                            </template>
                        </Dropdown>
                        <Badge
                            v-else
                            :label="t(`page.worker.modal.create.candidateStatus.${agency.candidateStatus}`)"
                            :color="BadgeColor.neutral"
                            stroke
                        />
                    </div>
                </div>
            </div>

            <div class="identification-worker-card__section__content-item">
                <div class="identification-worker-card__section__content-item-label">
                    <span class="typo-xxs-medium">
                        {{ t("form.phone") }}
                    </span>
                    <span
                        :class="[
                            'identification-worker-card__section__content-item-label-status',
                            matchClass(isPhoneMatch, 'identification-worker-card__section__content-item-label-status'),
                        ]"
                    >
                        <i :class="matchIcon(isPhoneMatch)" />
                    </span>
                </div>

                <div class="identification-worker-card__section__content-item-value">
                    <TextInputAlt :model-value="formatPhone(worker.phone)" icon-left="phone" variant="grey" state="filled" readonly />
                </div>
            </div>

            <div v-if="worker.email" class="identification-worker-card__section__content-item">
                <div class="identification-worker-card__section__content-item-label">
                    <span class="typo-xxs-medium">
                        {{ t("form.email") }}
                    </span>
                    <span
                        :class="[
                            'identification-worker-card__section__content-item-label-status',
                            matchClass(isEmailMatch, 'identification-worker-card__section__content-item-label-status'),
                        ]"
                    >
                        <i :class="matchIcon(isEmailMatch)" />
                    </span>
                </div>

                <div class="identification-worker-card__section__content-item-value">
                    <TextInputAlt :model-value="worker.email" icon-left="at" variant="grey" state="filled" readonly />
                </div>
            </div>

            <div v-if="worker.address.city" class="identification-worker-card__section__content-item">
                <div class="identification-worker-card__section__content-item-label">
                    <span class="typo-xxs-medium">
                        {{ t("form.address") }}
                    </span>
                </div>

                <div class="identification-worker-card__section__content-item-value">
                    <TextInputAlt :model-value="formattedAddress(worker.address)" icon-left="map-pin" variant="grey" state="filled" readonly />
                </div>
            </div>
        </div>

        <div v-if="hasDetails" class="identification-worker-card__section">
            <Link
                type="neutral-alt"
                size="xs"
                :label="t(showMoreDetails ? 'global.actions.reduce' : 'global.actions.viewMore')"
                :right-icon="showMoreDetails ? 'caret-down-filled' : 'caret-right-filled'"
                @click="showMoreDetails = !showMoreDetails"
            />

            <div v-if="showMoreDetails" class="identification-worker-card__section__content-items">
                <div v-if="worker.evoliaId" class="identification-worker-card__section__content-item">
                    <div class="identification-worker-card__section__content-item-label">
                        <span class="typo-xxs-medium">
                            {{ t("page.worker.modal.create.identification.labels.evolia") }}
                        </span>
                    </div>

                    <div class="identification-worker-card__section__content-item-value">
                        <TextInputAlt :model-value="worker.evoliaId.toString()" icon-left="database" variant="grey" state="filled" readonly />
                    </div>
                </div>

                <div v-if="worker.nir" class="identification-worker-card__section__content-item">
                    <div class="identification-worker-card__section__content-item-label">
                        <span class="typo-xxs-medium">
                            {{ t("page.worker.modal.create.identification.labels.nir") }}
                        </span>
                    </div>

                    <div class="identification-worker-card__section__content-item-value">
                        <TextInputAlt :model-value="worker.nir" icon-left="id" variant="grey" state="filled" readonly />
                    </div>
                </div>

                <div v-if="worker.birthDate" class="identification-worker-card__section__content-item">
                    <div class="identification-worker-card__section__content-item-label">
                        <span class="typo-xxs-medium">
                            {{ t("page.worker.modal.create.identification.labels.birthDate") }}
                        </span>
                        <span
                            :class="[
                                'identification-worker-card__section__content-item-label-status',
                                matchClass(isBirthDateMatch, 'identification-worker-card__section__content-item-label-status'),
                            ]"
                        >
                            <i :class="matchIcon(isBirthDateMatch)" />
                        </span>
                    </div>

                    <div class="identification-worker-card__section__content-item-value">
                        <TextInputAlt :model-value="formattedBirthDate" icon-left="cal-dot" variant="grey" state="filled" readonly />
                    </div>
                </div>

                <div v-if="worker.birthPlace" class="identification-worker-card__section__content-item">
                    <div class="identification-worker-card__section__content-item-label">
                        <span class="typo-xxs-medium">
                            {{ t("page.worker.modal.create.identification.labels.birthPlace") }}
                        </span>
                    </div>

                    <div class="identification-worker-card__section__content-item-value">
                        <TextInputAlt :model-value="worker.birthPlace" icon-left="world-pin" variant="grey" state="filled" readonly />
                    </div>
                </div>
            </div>
        </div>

        <ButtonAlt
            v-if="displayActionButton"
            class="identification-worker-card__button"
            :label="t('page.worker.modal.create.identification.buttons.seeProfil')"
            type="neutral"
            variant="stroke"
            full-width
            @click="handleViewWorkerProfile"
        />
    </div>
</template>

<style scoped lang="scss">
.identification-worker-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-xl, 16px);
    padding-top: var(--spacing-md, 8px);

    &__section {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--spacing-md, 8px);
        width: 100%;

        &__title {
            display: flex;
            align-items: flex-start;
            gap: var(--spacing-lg, 12px);
            padding-bottom: var(--spacing-lg, 12px);
            width: 100%;

            &-infos {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                flex: 1 0 0;
                gap: var(--spacing-xxs, 2px);
                max-width: 100%;

                &-name,
                &-description {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    max-width: 100%;
                }

                &-name {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-xs, 4px);
                    color: var(--text-main-900, #00061a);
                    &-status {
                        &--success {
                            color: var(--green-base, #00a355);
                        }
                        &--error {
                            color: var(--red-base, #e51940);
                        }
                    }
                }

                &-description {
                    color: var(--text-sub-600, #494e61);
                }
            }
        }

        &__content {
            &-items {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: var(--spacing-md, 8px);
            }

            &-item {
                display: flex;
                align-items: flex-start;
                width: 100%;

                &-label {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: var(--spacing-xs, 4px);
                    flex-shrink: 0;
                    width: 120px;
                    height: 28px;
                    color: var(--text-sub-600, #494e61);
                    &-status {
                        &--success {
                            color: var(--green-base, #00a355);
                        }
                        &--error {
                            color: var(--red-base, #e51940);
                        }
                    }
                }

                &-values {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: var(--spacing-xs, 4px);
                    flex: 1;
                    min-width: 0;
                }

                &-value {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-xs, 4px);
                    width: 100%;
                }
            }
        }

        .ds-link {
            padding: 0;
        }
    }
}
</style>
